// Ajustes de período anterior: corrigen roles ya pagados sumando/descontando
// en el próximo rol (borrador). Nómina → Movimientos → Ajustes.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, fieldCls, money, nomRpc, useDialogoEnLinea, type AjusteRow, type EmpleadoRow } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const ESTADOS: Record<string, string> = { pendiente: 'Pendiente', aplicado: 'Aplicado en rol', anulado: 'Anulado' };
const vacio = { empleado_id: '', tipo: 'ingreso', gravable: true, monto: '', periodo_afectado: '', motivo: '' };

export function Ajustes() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [estado, setEstado] = useState('pendiente');
  const { data: rows = [], isFetching } = useQuery<AjusteRow[]>({
    queryKey: ['nom-ajustes', estado],
    queryFn: () => nomRpc<AjusteRow[]>('nom_ajustes', { _estado: estado }),
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof vacio>(vacio);

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_ajuste_guardar', { _data: { ...form, monto: Number(form.monto), periodo_afectado: form.periodo_afectado || null } }),
    onSuccess: () => { toast.success('Ajuste registrado; se aplicará en el próximo rol en borrador'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-ajustes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => nomRpc('nom_ajuste_anular', { _id: id }),
    onSuccess: () => { toast.success('Ajuste anulado'); void qc.invalidateQueries({ queryKey: ['nom-ajustes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const headersExp = ['N°', 'Empleado', 'Tipo', 'Corrige', 'Motivo', 'Monto', 'Estado'];
  const MONEYCOLS = [5];
  const filasExport: Celda[][] = [
    ...rows.map((a, i): Celda[] => [
      i + 1, a.razon_social, a.tipo === 'ingreso' ? 'Ingreso' : 'Descuento', a.periodo_afectado ?? '—', a.motivo,
      a.tipo === 'egreso' ? -Number(a.monto) : Number(a.monto), ESTADOS[a.estado] ?? a.estado,
    ]),
    ['', '', '', '', 'TOTAL', rows.reduce((s, a) => s + (a.tipo === 'egreso' ? -Number(a.monto) : Number(a.monto)), 0), ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Ajustes de período anterior</h1>
        <Button onClick={() => { setForm(vacio); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Nuevo ajuste</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Corrige un rol ya pagado (que no se reabre) sumando o descontando en el próximo rol en borrador. El ingreso
        gravable afecta la base IESS/IR; el no gravable solo suma al neto; el egreso descuenta del neto.
      </p>

      <select className={`${fieldCls} w-56`} value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
        <option value="pendiente">Pendientes</option>
        <option value="aplicado">Aplicados</option>
        <option value="anulado">Anulados</option>
        <option value="todos">Todos</option>
      </select>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Ajustes ({rows.length})</CardTitle>
          <ExportarBotones
            nombre={`ajustes-nomina-${fechaLocal()}`} titulo="Ajustes de período anterior"
            headers={headersExp} filas={filasExport} colsDerecha={[0, ...MONEYCOLS]}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
            disabled={rows.length === 0} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Corrige</th>
              <th className="py-2 pr-3">Motivo</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin ajustes</td></tr>}
              {rows.map((a) => (
                <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40 align-top">
                  <td className="py-2 pr-3">{a.razon_social}<div className="text-xs text-muted-foreground font-mono">{a.identificacion}</div></td>
                  <td className="py-2 pr-3">
                    <span className={a.tipo === 'ingreso' ? 'text-estable' : 'text-urgente'}>{a.tipo === 'ingreso' ? 'Ingreso' : 'Descuento'}</span>
                    {a.tipo === 'ingreso' && <div className="text-xs text-muted-foreground">{a.gravable ? 'gravable' : 'no gravable'}</div>}
                  </td>
                  <td className="py-2 pr-3 font-mono">{a.periodo_afectado ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground max-w-xs">{a.motivo}{a.rol_codigo && <div className="text-xs">Aplicado en {a.rol_codigo}</div>}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums ${a.tipo === 'egreso' ? 'text-urgente' : ''}`}>{a.tipo === 'egreso' ? '−' : '+'}{money(a.monto)}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${a.estado === 'pendiente' ? 'bg-pendiente-soft text-pendiente' : a.estado === 'aplicado' ? 'bg-completado-soft text-completado' : 'bg-muted text-muted-foreground'}`}>
                      {ESTADOS[a.estado] ?? a.estado}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    {a.estado === 'pendiente' && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Anular"
                        onClick={async () => { if (await dlg.confirmar('¿Anular ajuste?', { peligro: true, aceptar: 'Anular' })) anular.mutate(a.id); }}><Ban className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nuevo ajuste de período anterior" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empleado *">
              <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Tipo *">
                <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <option value="ingreso">Ingreso (pagar de más)</option>
                  <option value="egreso">Descuento (recuperar)</option>
                </select>
              </Campo>
              <Campo label="Monto ($) *"><input type="number" min="0" step="0.01" className={fieldCls} value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} /></Campo>
            </div>
            {form.tipo === 'ingreso' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.gravable} onChange={(e) => setForm({ ...form, gravable: e.target.checked })} />
                Gravable (afecta aporte IESS e impuesto a la renta)
              </label>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Período que corrige (YYYY-MM)"><input className={fieldCls} value={form.periodo_afectado} placeholder="2026-08" onChange={(e) => setForm({ ...form, periodo_afectado: e.target.value })} /></Campo>
            </div>
            <Campo label="Motivo *"><input className={fieldCls} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Ej. Horas extra no pagadas en agosto" /></Campo>
            <div className="text-xs text-muted-foreground">Se aplicará automáticamente en el próximo rol que generes/recalcules en borrador.</div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.empleado_id || !Number(form.monto) || !form.motivo.trim()} onClick={() => guardar.mutate()}>Registrar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
